import { Trash2, Package, Clock, Plus } from 'lucide-react';
import { PartTypeahead } from './PartTypeahead';

/**
 * The one parts-and-labour editor, shared by quotes, jobs and diagnostics.
 *
 * WHY THIS EXISTS
 *
 * Each screen had its own copy of this table. That is why they looked different
 * from one another, and — more importantly — why data drifted as work moved
 * along the chain: a diagnostic's "finding" had to be TRANSLATED into a quote
 * line, and a quote line into a job item, and every translation is a chance to
 * lose a column. Sharing the editor makes conversion a copy instead.
 *
 * WHY IT IS NOT A 12-COLUMN GRID ON A PHONE
 *
 * The old markup was `grid-cols-12` at every width. At 320px a `col-span-1`
 * track is about 25 pixels, so Qty and the delete button were thumbnail-sized
 * and the bin ended up printed on top of the line total ("£50.0🗑"). Below `sm`
 * each line is now a card with real labels and full-size controls; the table
 * appears from `sm:` up, where there is room for it.
 */

export type LineItemType = 'part' | 'labour';

export interface EditableLineItem {
    id: string;
    type: LineItemType;
    description: string;
    quantity: number;
    unitPrice: number;
    costPrice?: number;
}

interface Props {
    items: EditableLineItem[];
    onUpdate: (id: string, patch: Partial<EditableLineItem>) => void;
    onRemove: (id: string) => void;
    onAdd: (type: LineItemType) => void;
    /** Percent added to a part's cost to reach its sell price. 0 disables the hint. */
    partsMarkup?: number;
    /** Shown under the rows. Omit to render the editor without totals. */
    subtotal?: number;
    vat?: number;
    total?: number;
    /** Labour rows price by the hour, so the qty label changes. */
    labourUnitLabel?: string;
}

const money = (n: number) => `£${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

const numberInput =
    'w-full bg-canvas border border-line rounded-lg text-fg ' +
    'text-sm font-mono outline-none focus:border-gold transition-colors py-2 px-2 min-h-[44px] sm:min-h-0 sm:py-1';

export function LineItemsEditor({
    items, onUpdate, onRemove, onAdd,
    partsMarkup = 0, subtotal, vat, total,
    labourUnitLabel = 'hrs',
}: Props) {
    const showTotals = subtotal !== undefined;

    return (
        <div className="space-y-3 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-fg">Parts &amp; labour</h3>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => onAdd('part')}
                        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg bg-info/10 border border-info/30 text-info text-sm font-bold hover:bg-info/20 transition-colors"
                    >
                        <Plus className="w-4 h-4" /><Package className="w-4 h-4" /> Part
                    </button>
                    <button
                        type="button"
                        onClick={() => onAdd('labour')}
                        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg bg-gold-fill/10 border border-gold/35 text-gold text-sm font-bold hover:brightness-125 transition-colors"
                    >
                        <Plus className="w-4 h-4" /><Clock className="w-4 h-4" /> Labour
                    </button>
                </div>
            </div>

            {items.length === 0 ? (
                <p className="text-sm text-fg-3 py-6 text-center border border-dashed border-line rounded-lg">
                    Nothing added yet. Add a part or some labour.
                </p>
            ) : (
                <>
                    {/* Table header — only where the table exists. */}
                    <div className="hidden sm:grid grid-cols-12 gap-2 text-fd-3xs font-bold text-fg-3 uppercase tracking-wider px-2">
                        <span className="col-span-2">Type</span>
                        <span className="col-span-3">Description</span>
                        <span className="col-span-2 text-right">Cost</span>
                        <span className="col-span-1 text-center">Qty</span>
                        <span className="col-span-2 text-right">Sell</span>
                        <span className="col-span-1 text-right">Total</span>
                        <span className="col-span-1" />
                    </div>

                    <div className="space-y-2">
                        {items.map((item) => {
                            const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
                            const isPart = item.type === 'part';
                            return (
                                <div
                                    key={item.id}
                                    className="rounded-lg bg-card border border-line p-3 sm:p-2 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center min-w-0"
                                >
                                    {/* Type + description */}
                                    <div className="sm:col-span-2 flex items-center gap-2 mb-2 sm:mb-0 min-w-0">
                                        <span className={`inline-flex items-center gap-1 text-fd-3xs font-bold uppercase px-2 py-1 rounded ${
                                            isPart ? 'bg-info/15 text-info' : 'bg-gold-fill/10 text-gold'
                                        }`}>
                                            {isPart ? <Package className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                            {isPart ? 'Part' : 'Labour'}
                                        </span>
                                    </div>

                                    {/* Was a bare text box. Every part name in the
                                        workshop was typed out in full, every time.
                                        It is still a text box — it just offers what
                                        this org has used before, and a shared
                                        dictionary, while you type. */}
                                    <PartTypeahead
                                        value={item.description}
                                        onChange={(v) => onUpdate(item.id, { description: v })}
                                        onPick={(part) => onUpdate(item.id, {
                                            description: part.name,
                                            // Only fill a price the user has not already
                                            // set: re-picking a part must not silently
                                            // overwrite a figure typed for this job.
                                            ...(part.retailPrice != null && !item.unitPrice
                                                ? { unitPrice: Number(part.retailPrice) } : {}),
                                            ...(part.costPrice != null && !item.costPrice
                                                ? { costPrice: Number(part.costPrice) } : {}),
                                        })}
                                        itemType={isPart ? 'part' : 'labour'}
                                        placeholder={isPart ? 'Start typing a part…' : 'What was done…'}
                                        className="sm:col-span-3 mb-2 sm:mb-0"
                                    />

                                    {/* Numbers. Labelled on a phone, headed by the
                                        table header from sm: up. */}
                                    <div className="grid grid-cols-3 gap-2 sm:contents">
                                        <label className="flex flex-col gap-1 sm:col-span-2 min-w-0">
                                            <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-3 font-display">
                                                Cost
                                            </span>
                                            {isPart ? (
                                                <input
                                                    type="number" inputMode="decimal" step="0.01" min={0}
                                                    value={item.costPrice || ''}
                                                    onChange={(e) => onUpdate(item.id, { costPrice: parseFloat(e.target.value) || 0 })}
                                                    className={`${numberInput} sm:text-right`}
                                                    title={partsMarkup > 0 ? `What you paid — ${partsMarkup}% markup is added to reach Sell` : 'What you paid'}
                                                />
                                            ) : (
                                                <span className="hidden sm:block" />
                                            )}
                                        </label>

                                        <label className="flex flex-col gap-1 sm:col-span-1 min-w-0">
                                            <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-3 font-display">
                                                {isPart ? 'Qty' : labourUnitLabel}
                                            </span>
                                            <input
                                                type="number" inputMode="decimal"
                                                step={isPart ? '1' : '0.25'} min={0}
                                                value={item.quantity || ''}
                                                onChange={(e) => onUpdate(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                                className={`${numberInput} sm:text-center`}
                                            />
                                        </label>

                                        <label className="flex flex-col gap-1 sm:col-span-2 min-w-0">
                                            <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-3 font-display">
                                                Sell
                                            </span>
                                            <input
                                                type="number" inputMode="decimal" step="0.01" min={0}
                                                value={item.unitPrice || ''}
                                                onChange={(e) => onUpdate(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                                                className={`${numberInput} sm:text-right`}
                                            />
                                        </label>
                                    </div>

                                    {/* Total and delete. On a phone these get their
                                        own row — the bin used to be a 25px target
                                        rendered on top of the total. */}
                                    <div className="sm:contents flex items-center justify-between gap-3 mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-line">
                                        <span className="sm:col-span-1 text-sm font-mono font-bold text-fg sm:text-right tabular-nums">
                                            <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-3 font-display mr-2">Total</span>
                                            {money(lineTotal)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => onRemove(item.id)}
                                            aria-label="Remove this line"
                                            className="sm:col-span-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-0 sm:min-h-0 sm:py-1 rounded-lg text-danger bg-danger/10 sm:bg-transparent hover:bg-danger/20 transition-colors text-sm font-semibold"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            <span className="sm:hidden">Remove</span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {showTotals && items.length > 0 && (
                <div className="border-t border-line pt-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                        <span className="text-fg-3">Subtotal</span>
                        <span className="font-mono text-fg tabular-nums">{money(subtotal!)}</span>
                    </div>
                    {vat !== undefined && (
                        <div className="flex justify-between text-sm">
                            <span className="text-fg-3">VAT</span>
                            <span className="font-mono text-fg tabular-nums">{money(vat)}</span>
                        </div>
                    )}
                    {total !== undefined && (
                        <div className="flex justify-between pt-2 border-t border-line">
                            <span className="font-bold text-fg">Total</span>
                            <span className="font-mono font-bold text-lg text-gold tabular-nums">{money(total)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
